import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../app.tsx';
import DisabledOverlay from './disabled-overlay.tsx';
import SaveSpecificationModal from './save-specification-modal.tsx';

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

/**
 * The restart control on the vehicle panel: an icon at the bottom right of the canvas overlay that
 * opens a small popup offering 'Re-start Configuration' and 'Save Specification'. It balances the
 * settings toggles at the bottom left, and on mobile sits to the left of the orientation hint.
 */
export default function RestartOptions() {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// refs.
	const containerRef = useRef<HTMLDivElement>(null);

	// state.
	const [isOpen, setIsOpen] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);

	// handlers.

	const toggleOptions = () => setIsOpen(!isOpen);

	// close the popup before restarting, so it isn't left open behind the home screen.
	const handleRestartClicked = () => {
		setIsOpen(false);
		context.restartConfiguration();
	}

	useEffect(() => {

		// nothing to listen for while the popup is closed.
		if (!isOpen) {
			return;
		}

		// close the popup if anything outside it is clicked.
		const handleClickOutside = (event: globalThis.MouseEvent | TouchEvent) => {
			const target = event.target as Node | null;
			if (!target) return;

			if (containerRef.current && !containerRef.current.contains(target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('touchstart', handleClickOutside);

		// cleanup listener on close/unmount.
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('touchstart', handleClickOutside);
		};
	}, [isOpen]);

	// return the HTML.
	return (
		<div id="vehicle-restart" ref={containerRef}>
			<a role="button" id="vehicle-restart-trigger" onClick={toggleOptions}>
				<img src="assets/images/icons/restart.svg" width="34" height="34" alt="Configuration options" />
			</a>

			{
				/*
					the popup is unmounted when closed, which also unmounts the PDF modal nested inside it - so the
					popup is deliberately left open while that modal is up. the modal's own blurred backdrop covers
					it, and the click-outside handler tidies it away afterwards.
				*/
				isOpen && (
					<div id="vehicle-restart-options">
						<button className="vehicle-restart-option" onClick={handleRestartClicked}>
							Re-start Configuration
						</button>
						<SaveSpecificationModal
							cameraController={context.cameraController}
							setIsDisabled={setIsDisabled}
							trigger={(openModal: () => void) => (
								<button className="vehicle-restart-option" onClick={openModal}>
									Save Specification
								</button>
							)}
						/>
					</div>
				)
			}

			{isDisabled && <DisabledOverlay />}
		</div>
	);
}
